// src/utils/exportProyectoExcel.js
// ============================================================
// Exports a full project report to Excel with styled headers,
// dual currency columns, and profit distribution details.
// ============================================================

import * as XLSX from "xlsx-js-style";
import { toast } from "react-toastify";
import axios from "axios";
import { formatNumber } from "../components/utils";
import { getAuthHeader } from "./auth";
import {
  getStagePercentages,
  getAssignmentPoolPercent,
  isExcludedFromAssignment,
} from "./asignacion-utilidad";
import { API_URL_utilidad } from "../constants";

// ── Style constants ──────────────────────────────────────────
const LSSI_BLUE = "1A3C6E";
const LSSI_GREEN = "28A745";
const LSSI_RED = "DC3545";
const LSSI_TEAL = "17A2B8";
const LSSI_PURPLE = "6F42C1";
const LSSI_ORANGE = "FD7E14";
const LIGHT_BLUE = "D6E4F0";
const WHITE = "FFFFFF";

const CAT_COLORS = {
  Preventa: "3B82F6",
  Comercial: "22C55E",
  "Post Venta": "EF4444",
  Coaching: "F59E0B",
};

// ── Cell helpers ─────────────────────────────────────────────

const sectionHeader = (text, cols = 7) => {
  const row = [];
  for (let i = 0; i < cols; i++) {
    row.push({
      v: i === 0 ? text : "",
      s: {
        font: { bold: true, color: { rgb: WHITE }, sz: 13 },
        fill: { fgColor: { rgb: LSSI_BLUE } },
        alignment: { horizontal: i === 0 ? "left" : "center", vertical: "center" },
        border: { bottom: { style: "thin", color: { rgb: "000000" } } },
      },
    });
  }
  return row;
};

const subHeaderRow = (text, color, cols = 5) => {
  const row = [];
  for (let i = 0; i < cols; i++) {
    row.push({
      v: i === 0 ? text : "",
      s: {
        font: { bold: true, color: { rgb: WHITE }, sz: 11 },
        fill: { fgColor: { rgb: color } },
        alignment: { horizontal: "center", vertical: "center" },
        border: { bottom: { style: "thin", color: { rgb: "999999" } } },
      },
    });
  }
  return row;
};

const colHeader = (text) => ({
  v: text,
  s: {
    font: { bold: true, color: { rgb: "333333" }, sz: 10 },
    fill: { fgColor: { rgb: LIGHT_BLUE } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: {
      bottom: { style: "thin", color: { rgb: "999999" } },
      top: { style: "thin", color: { rgb: "999999" } },
    },
  },
});

const labelCell = (text) => ({
  v: text,
  s: { font: { bold: true, color: { rgb: "333333" }, sz: 10 }, alignment: { horizontal: "left" } },
});

const valCell = (val) => ({
  v: val ?? "",
  s: { font: { sz: 10 }, alignment: { horizontal: "left" } },
});

const numCell = (val, color = null) => ({
  v: Number(val) || 0,
  t: "n",
  s: {
    font: { sz: 10, color: color ? { rgb: color } : undefined },
    numFmt: "#,##0.00",
    alignment: { horizontal: "right" },
  },
});

const pctCell = (val) => ({
  v: Number(val) || 0,
  t: "n",
  s: {
    font: { sz: 10, bold: true },
    numFmt: '0.00"%"',
    alignment: { horizontal: "right" },
  },
});

const currencyCell = (val, currency) => ({
  v: `${currency} ${formatNumber(val || 0)}`,
  s: { font: { sz: 10, color: { rgb: "666666" } }, alignment: { horizontal: "right" } },
});

const badgeCell = (text, bgColor) => ({
  v: text,
  s: {
    font: { bold: true, color: { rgb: WHITE }, sz: 9 },
    fill: { fgColor: { rgb: bgColor } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: bgColor } },
      bottom: { style: "thin", color: { rgb: bgColor } },
      left: { style: "thin", color: { rgb: bgColor } },
      right: { style: "thin", color: { rgb: bgColor } },
    },
  },
});

const totalRowStyled = (cells, color = LSSI_BLUE) =>
  cells.map((c, i) => {
    if (typeof c === "number") {
      return {
        v: c,
        t: "n",
        s: {
          font: { bold: true, color: { rgb: WHITE }, sz: 11 },
          fill: { fgColor: { rgb: color } },
          numFmt: "#,##0.00",
          alignment: { horizontal: "right" },
        },
      };
    }
    if (typeof c === "string" && c.endsWith("%")) {
      return {
        v: parseFloat(c) || 0,
        t: "n",
        s: {
          font: { bold: true, color: { rgb: WHITE }, sz: 11 },
          fill: { fgColor: { rgb: color } },
          numFmt: '0.00"%"',
          alignment: { horizontal: "right" },
        },
      };
    }
    return {
      v: c ?? "",
      s: {
        font: { bold: true, color: { rgb: WHITE }, sz: 11 },
        fill: { fgColor: { rgb: color } },
        alignment: { horizontal: i === 0 ? "left" : "center" },
      },
    };
  });

// ── Distribution calculator ──────────────────────────────────

function calculateDistribution({ utilidad, utilities, rawUtilidades, instructores, filteredLider, proyectoLider, loadedLeader }) {
  const utilizadNum = Number(utilidad) || 0;

  const STAGE_KEYS = [
    "contacto", "cita", "diagnostico", "propuesta", "cierre",
    "kickoff", "seguimiento", "reunioncierre", "coaching",
  ];

  const STAGE_DISPLAY_NAMES = {
    contacto: "Contacto",
    cita: "Cita",
    diagnostico: "Diagnóstico",
    propuesta: "Propuesta",
    cierre: "Cierre",
    kickoff: "Kickoff",
    seguimiento: "Seguimiento",
    reunioncierre: "Reunión de Cierre",
    coaching: "Coaching/Líder",
  };

  // Map legacy DB keys to normalized keys
  const KEY_ALIASES = {
    "Contacto": "contacto",
    "Cita": "cita",
    "Diagnóstico": "diagnostico",
    "Diagnostico": "diagnostico",
    "Propuesta": "propuesta",
    "Cierre": "cierre",
    "Kickoff": "kickoff",
    "Seguimiento": "seguimiento",
    "Reunión de Cierre": "reunioncierre",
    "Reunion de Cierre": "reunioncierre",
    "ReunionCierre": "reunioncierre",
    "Coaching/Lider": "coaching",
    "Coaching": "coaching",
  };

  const getStageCategory = (key) => {
    if (["contacto", "cita"].includes(key)) return "Preventa";
    if (["diagnostico", "propuesta", "cierre"].includes(key)) return "Comercial";
    if (key === "coaching") return "Coaching";
    return "Post Venta";
  };

  // Normalize a tipo_utilidad value to a known stage key
  const normalizeKey = (raw) => {
    if (!raw) return null;
    const trimmed = raw.trim();
    if (KEY_ALIASES[trimmed]) return KEY_ALIASES[trimmed];
    const lower = trimmed.toLowerCase();
    if (lower in getStagePercentages()) return lower;
    return null;
  };

  // 1. Stage breakdown — use rawUtilidades (has tipo_utilidad per record)
  const stageBreakdown = [];
  const stagePercentages = getStagePercentages();
  let totalStages = 0;

  // Filter rawUtilidades to stage records only (exclude "Asignacion" entries)
  const stageRecords = (rawUtilidades || []).filter((u) => normalizeKey(u.tipo_utilidad) !== null);

  if (stageRecords.length > 0) {
    stageRecords.forEach((util) => {
      const key = normalizeKey(util.tipo_utilidad);
      const partner = util.partner || "";
      const percent = Number(util.porcentaje) || stagePercentages[key] || 0;
      // Recalculate amount from current utilidad × percentage (most accurate)
      const amount = utilizadNum > 0 ? utilizadNum * (percent / 100) : (Number(util.cantidad) || 0);

      if (partner && key) {
        stageBreakdown.push({
          stage: STAGE_DISPLAY_NAMES[key] || key,
          partner,
          percent,
          amount,
          category: getStageCategory(key),
        });
        totalStages += amount;
      }
    });
  } else if (utilizadNum > 0) {
    // Fallback: generate from leader assignment (matching UI behavior)
    // Try to find the leader partner from filteredLider or loadedLeader
    let leaderPartner = loadedLeader || proyectoLider || null;

    // Check filteredLider for stage assignments
    if (filteredLider && filteredLider.length > 0) {
      const liderRecord = filteredLider[0];
      // Each stage might have a partner assigned in the lider record
      STAGE_KEYS.forEach((key) => {
        const partner = liderRecord[key] || leaderPartner;
        const percent = stagePercentages[key] || 0;
        if (partner && percent > 0) {
          const amount = utilizadNum * (percent / 100);
          stageBreakdown.push({
            stage: STAGE_DISPLAY_NAMES[key] || key,
            partner,
            percent,
            amount,
            category: getStageCategory(key),
          });
          totalStages += amount;
        }
      });
    } else if (leaderPartner) {
      // All stages assigned to the leader
      STAGE_KEYS.forEach((key) => {
        const percent = stagePercentages[key] || 0;
        if (percent > 0) {
          const amount = utilizadNum * (percent / 100);
          stageBreakdown.push({
            stage: STAGE_DISPLAY_NAMES[key] || key,
            partner: leaderPartner,
            percent,
            amount,
            category: getStageCategory(key),
          });
          totalStages += amount;
        }
      });
    }
  }

  // 2. Instructor assignments
  // Mirror detalle_proyecto.js instructorsWithProportions: prefer tarifa_hora,
  // but fall back to costo_hora when no instructor in the project has tarifa_hora set.
  // Without this fallback, projects that only track costo_hora produce zero income
  // and the entire assignment section gets skipped from the export.
  const assignments = [];
  const assignmentPoolPercent = getAssignmentPoolPercent();
  const assignmentPool = utilizadNum * (assignmentPoolPercent / 100);
  let totalEligibleIncome = 0;
  const incomeByPartner = {};

  const anyTarifaHora = (instructores || []).some(
    (inst) => Number(inst?.tarifa_hora || 0) > 0
  );

  (instructores || []).forEach((inst) => {
    const partner = (inst.miembro_que_asigna || "").trim();
    if (!partner || isExcludedFromAssignment(partner)) return;
    const rate = anyTarifaHora && Number(inst.tarifa_hora || 0) > 0
      ? Number(inst.tarifa_hora || 0)
      : Number(inst.costo_hora || 0);
    const income = Number(inst.horas || 0) * Number(inst.semanas || 0) * rate;
    incomeByPartner[partner] = (incomeByPartner[partner] || 0) + income;
    totalEligibleIncome += income;
  });

  if (totalEligibleIncome > 0 && assignmentPool > 0) {
    Object.entries(incomeByPartner).forEach(([partner, partnerIncome]) => {
      const proportion = partnerIncome / totalEligibleIncome;
      const commission = proportion * assignmentPool;
      assignments.push({
        partner,
        income: partnerIncome,
        proportion: proportion * 100,
        commission,
        percentOfUtilidad: (commission / (utilizadNum || 1)) * 100,
      });
    });
  }

  // 3. Partner summary
  const partnerSummary = {};

  stageBreakdown.forEach(({ partner, amount: amt, percent: pct }) => {
    if (!partnerSummary[partner]) partnerSummary[partner] = { stageTotal: 0, stagePercent: 0, assignmentTotal: 0, assignmentPercent: 0, grandTotal: 0, grandPercent: 0 };
    partnerSummary[partner].stageTotal += amt;
    partnerSummary[partner].stagePercent += pct;
  });

  assignments.forEach(({ partner, commission, percentOfUtilidad }) => {
    if (!partnerSummary[partner]) partnerSummary[partner] = { stageTotal: 0, stagePercent: 0, assignmentTotal: 0, assignmentPercent: 0, grandTotal: 0, grandPercent: 0 };
    partnerSummary[partner].assignmentTotal += commission;
    partnerSummary[partner].assignmentPercent += percentOfUtilidad;
  });

  Object.keys(partnerSummary).forEach((partner) => {
    const p = partnerSummary[partner];
    p.grandTotal = p.stageTotal + p.assignmentTotal;
    p.grandPercent = utilizadNum > 0 ? (p.grandTotal / utilizadNum) * 100 : 0;
  });

  const totalPartnersAmount = Object.values(partnerSummary).reduce((sum, p) => sum + p.grandTotal, 0);
  const totalPartnersPercent = utilizadNum > 0 ? (totalPartnersAmount / utilizadNum) * 100 : 0;
  const lssiRemaining = utilizadNum - totalPartnersAmount;
  const lssiRemainingPercent = utilizadNum > 0 ? (lssiRemaining / utilizadNum) * 100 : 0;

  return {
    utilidad: utilizadNum,
    stageBreakdown,
    totalStages,
    totalStagesPercent: utilizadNum > 0 ? (totalStages / utilizadNum) * 100 : 0,
    assignments,
    totalAssignments: assignmentPool,
    totalAssignmentsPercent: assignmentPoolPercent,
    partnerSummary,
    totalPartnersAmount,
    totalPartnersPercent,
    lssiRemaining,
    lssiRemainingPercent,
  };
}

// ── Breakdown helpers ────────────────────────────────────────

const fmtBreakdown = (obj) => {
  if (!obj || typeof obj !== "object") return "";
  return Object.entries(obj)
    .filter(([, v]) => v)
    .map(([cur, amt]) => `${cur} ${formatNumber(amt)}`)
    .join(" | ");
};

const getOriginal = (cbObj, currency) => {
  if (!cbObj || !currency || currency === "USD") return null;
  return cbObj[currency] || null;
};

// ══════════════════════════════════════════════════════════════
// Main export function
// ══════════════════════════════════════════════════════════════

export default async function exportProyectoExcel({
  selectedProyecto,
  summary,
  instructores,
  partidas,
  partidasCurso,
  estudiantesAbierto,
  ingresoGeneral,
  comisionVenta,
  proyectoConCausa,
  utilities,
  utilidadesByProject,
  abiertoMiembros,
  filteredLider,
  loadedLeader,
  primaryCurrency,
  profitByCurrency,
  profitInOriginalCurrency,
  revenueInOriginalCurrency,
  costInOriginalCurrency,
}) {
  if (!selectedProyecto) return;

  // Fetch raw utilidad records (with tipo_utilidad / stage detail)
  let rawUtilidades = [];
  try {
    const headers = { Authorization: getAuthHeader() };
    const resp = await axios.get(API_URL_utilidad, { headers });
    rawUtilidades = (resp.data || []).filter(
      (u) => u.proyecto_code === selectedProyecto.proyecto_code
    );
  } catch (e) {
    console.warn("Could not fetch raw utilidades for export:", e);
  }

  const detalle = summary?.detalle || {};
  const cb = summary?.currency_breakdown || {};
  const pCur = primaryCurrency || selectedProyecto?.moneda || "USD";
  const showDualCurrency = pCur && pCur !== "USD";
  const origRev = revenueInOriginalCurrency;
  const origCost = costInOriginalCurrency;
  const origProfit = profitInOriginalCurrency;
  const utilidad = summary?.utilidad || 0;

  const rows = [];
  const merges = [];
  let r = 0;

  const addMerge = (startCol, endCol) => {
    merges.push({ s: { r, c: startCol }, e: { r, c: endCol } });
  };

  const addSection = (text, cols = 10) => {
    rows.push(sectionHeader(text, cols));
    addMerge(0, cols - 1);
    r++;
  };

  const addSub = (text, color, cols = 5) => {
    rows.push(subHeaderRow(text, color, cols));
    addMerge(0, cols - 1);
    r++;
  };

  const addBlank = () => {
    rows.push([]);
    r++;
  };

  // ══════════════════════════════════════════════════════════
  // 1. PROJECT HEADER
  // ══════════════════════════════════════════════════════════
  addSection("RESUMEN DEL PROYECTO", 3);

  const projectFields = [
    ["Nombre", selectedProyecto.name],
    ["Código", selectedProyecto.proyecto_code],
    ["Modalidad", selectedProyecto.modalidad || "—"],
    ["Líder", loadedLeader || selectedProyecto.lider || "—"],
    ["Status", selectedProyecto.status || "—"],
    ["Moneda Original", pCur],
    ["Value Stream", selectedProyecto.sede || "—"],
    ["País", selectedProyecto.pais || "—"],
    ["Ciudad", selectedProyecto.ciudad || "—"],
    ["Fecha Inicio", selectedProyecto.inicio_date ? new Date(selectedProyecto.inicio_date).toLocaleDateString("es-MX") : "—"],
    ["Fecha Fin", selectedProyecto.terminacion_date ? new Date(selectedProyecto.terminacion_date).toLocaleDateString("es-MX") : "—"],
  ];
  projectFields.forEach(([label, val]) => {
    rows.push([labelCell(label), valCell(val)]);
    r++;
  });
  addBlank();

  // ══════════════════════════════════════════════════════════
  // 2. FINANCIAL SUMMARY
  // ══════════════════════════════════════════════════════════
  addSection("RESUMEN FINANCIERO", 4);

  rows.push([
    colHeader("Métrica"),
    colHeader("USD Total"),
    colHeader(showDualCurrency ? `${pCur} Original` : ""),
    colHeader("Desglose"),
  ]);
  r++;

  rows.push([
    labelCell("Ingreso Total"),
    numCell(summary?.ingreso || 0, LSSI_GREEN),
    showDualCurrency && origRev ? currencyCell(origRev, pCur) : valCell(""),
    valCell(""),
  ]);
  r++;
  rows.push([
    labelCell("Costo Total"),
    numCell(summary?.costo || 0, LSSI_RED),
    showDualCurrency && origCost ? currencyCell(origCost, pCur) : valCell(""),
    valCell(""),
  ]);
  r++;
  rows.push([
    labelCell("Utilidad"),
    numCell(utilidad, LSSI_TEAL),
    showDualCurrency && origProfit ? currencyCell(origProfit, pCur) : valCell(""),
    valCell(""),
  ]);
  r++;
  rows.push([labelCell("Margen %"), pctCell(summary?.margen || 0), valCell(""), valCell("")]);
  r++;
  addBlank();

  // ══════════════════════════════════════════════════════════
  // 3. REVENUE / COST BREAKDOWN
  // ══════════════════════════════════════════════════════════
  addSection("DESGLOSE INGRESO / COSTO", 4);
  rows.push([
    colHeader("Concepto"),
    colHeader("USD"),
    colHeader(showDualCurrency ? `${pCur} Original` : "Original"),
    colHeader("Desglose Completo"),
  ]);
  r++;

  const breakdownItems = [
    ["Ingreso Instructores", detalle.ingreso_instructores || 0, cb.ingreso_instructores],
    ["Costo Instructores", detalle.costo_instructores || 0, cb.costo_instructores],
    ["Ingreso Curso Cerrado", detalle.ingreso_curso_cerrado || 0, cb.ingreso_curso_cerrado],
    ["Ingreso Curso Abierto", detalle.ingreso_abierto || 0, cb.ingreso_abierto],
    ["Ingreso Partidas", detalle.partidas_ingreso || 0, cb.partidas_ingreso],
    ["Costo Partidas", detalle.partidas_costo || 0, cb.partidas_costo],
    ["Ingreso General", detalle.ingreso_general || 0, null],
    ["Admin Curso Abierto", detalle.admin_curso_abierto || 0, null],
    ["Admin Curso Cerrado", detalle.admin_curso_cerrado || 0, null],
    ["Comisión de Venta", detalle.comision_venta || 0, null],
    ["Comisión Abierto", detalle.comision_abierto || 0, null],
    ["Proyecto con Causa", detalle.proyecto_con_causa || 0, null],
  ];

  breakdownItems
    .filter(([, usd]) => (Number(usd) || 0) !== 0)
    .forEach(([label, usd, cbObj]) => {
      const origAmt = getOriginal(cbObj, pCur);
      rows.push([
        labelCell(label),
        numCell(usd),
        origAmt ? currencyCell(origAmt, pCur) : valCell(""),
        valCell(fmtBreakdown(cbObj)),
      ]);
      r++;
    });

  // Totals for breakdown
  rows.push(totalRowStyled([
    "INGRESO TOTAL",
    summary?.ingreso || 0,
    showDualCurrency && origRev ? `${pCur} ${formatNumber(origRev)}` : "",
    "",
  ], LSSI_GREEN));
  r++;
  rows.push(totalRowStyled([
    "COSTO TOTAL",
    summary?.costo || 0,
    showDualCurrency && origCost ? `${pCur} ${formatNumber(origCost)}` : "",
    "",
  ], LSSI_RED));
  r++;
  rows.push(totalRowStyled([
    "UTILIDAD",
    utilidad,
    showDualCurrency && origProfit ? `${pCur} ${formatNumber(origProfit)}` : "",
    "",
  ], LSSI_TEAL));
  r++;
  addBlank();

  // ══════════════════════════════════════════════════════════
  // 4. INSTRUCTORES
  // ══════════════════════════════════════════════════════════
  addSection(`INSTRUCTORES (${instructores.length} registros)`, 10);
  if (instructores.length > 0) {
    rows.push([
      colHeader("Instructor"), colHeader("Email"), colHeader("Semanas"),
      colHeader("Horas"), colHeader("Costo Hora"), colHeader("Tarifa Hora"),
      colHeader("Costo Total"), colHeader("Ingreso Total"), colHeader("Moneda"),
      colHeader("Miembro que Asigna"),
    ]);
    r++;

    let totalCostoInst = 0;
    let totalIngresoInst = 0;

    instructores.forEach((inst) => {
      const costoTotal = (inst.semanas || 0) * (inst.horas || 0) * (inst.costo_hora || 0);
      const ingresoTotal = (inst.semanas || 0) * (inst.horas || 0) * (inst.tarifa_hora || inst.costo_hora || 0);
      totalCostoInst += costoTotal;
      totalIngresoInst += ingresoTotal;

      rows.push([
        valCell(inst.instructor_name || inst.instructor || ""),
        valCell(inst.email || ""),
        numCell(inst.semanas || 0),
        numCell(inst.horas || 0),
        numCell(inst.costo_hora || 0),
        numCell(inst.tarifa_hora || 0),
        numCell(costoTotal),
        numCell(ingresoTotal),
        valCell(inst.moneda || ""),
        valCell(inst.miembro_que_asigna || ""),
      ]);
      r++;
    });

    rows.push(totalRowStyled([
      "TOTAL", "", "", "", "", "",
      totalCostoInst, totalIngresoInst,
      showDualCurrency ? pCur : "", "",
    ], LSSI_BLUE));
    r++;

    if (showDualCurrency) {
      rows.push([
        labelCell("Equivalente USD"),
        valCell(""), valCell(""), valCell(""), valCell(""), valCell(""),
        numCell(detalle.costo_instructores || 0, LSSI_TEAL),
        numCell(detalle.ingreso_instructores || 0, LSSI_GREEN),
        valCell("USD"), valCell(""),
      ]);
      r++;
    }
  }
  addBlank();

  // ══════════════════════════════════════════════════════════
  // 5. PARTIDAS DIRECTO
  // ══════════════════════════════════════════════════════════
  addSection(`PARTIDAS DIRECTO (${partidas.length} registros)`, 11);
  if (partidas.length > 0) {
    rows.push([
      colHeader("Partida"), colHeader("Producto"), colHeader("Proveedor"),
      colHeader("Asignado"), colHeader("Instructor"), colHeader("Precio\nUnitario"),
      colHeader("Costo\nUnitario"), colHeader("Cantidad"), colHeader("Precio\nTotal"),
      colHeader("Costo\nTotal"), colHeader("Moneda"),
    ]);
    r++;

    let totalPrecio = 0;
    let totalCosto = 0;

    partidas.forEach((p) => {
      const precioTotal = Number(p.totalPrice) || (Number(p.precio || 0) * Number(p.cantidad || 0));
      const costoTotal = Number(p.totalCost) || (Number(p.costo || 0) * Number(p.cantidad || 0));
      totalPrecio += precioTotal;
      totalCosto += costoTotal;

      rows.push([
        valCell(p.partida || ""),
        valCell(p.producto || ""),
        valCell(p.proveedor || ""),
        valCell(p.asignado || ""),
        valCell(p.miembro_asignado || ""),
        numCell(p.precio || 0),
        numCell(p.costo || 0),
        numCell(p.cantidad || 0),
        numCell(precioTotal),
        numCell(costoTotal),
        valCell(p.moneda || ""),
      ]);
      r++;
    });

    rows.push(totalRowStyled([
      "TOTAL", "", "", "", "", "", "", "",
      totalPrecio, totalCosto, showDualCurrency ? pCur : "",
    ], LSSI_BLUE));
    r++;

    if (showDualCurrency) {
      rows.push([
        labelCell("Equivalente USD"),
        valCell(""), valCell(""), valCell(""), valCell(""), valCell(""), valCell(""), valCell(""),
        numCell(detalle.partidas_ingreso || 0, LSSI_GREEN),
        numCell(detalle.partidas_costo || 0, LSSI_RED),
        valCell("USD"),
      ]);
      r++;
    }
  }
  addBlank();

  // ══════════════════════════════════════════════════════════
  // 6. CURSO CERRADO
  // ══════════════════════════════════════════════════════════
  if (partidasCurso.length > 0) {
    addSection(`CURSO CERRADO - ESTUDIANTES (${partidasCurso.length} registros)`, 6);
    rows.push([
      colHeader("Concepto"), colHeader("Descripción"), colHeader("Cliente"),
      colHeader("Ingreso"), colHeader("Moneda"), colHeader("USD Equiv."),
    ]);
    r++;

    partidasCurso.forEach((pc) => {
      rows.push([
        valCell(pc.concepto || ""),
        valCell(pc.descripcion || ""),
        valCell(pc.cliente || ""),
        numCell(pc.ingreso || pc.precio || 0),
        valCell(pc.moneda || ""),
        valCell(""),
      ]);
      r++;
    });

    rows.push(totalRowStyled(["TOTAL", "", "", detalle.ingreso_curso_cerrado || 0, "USD", ""], LSSI_BLUE));
    r++;
    addBlank();
  }

  // ══════════════════════════════════════════════════════════
  // 7. CURSO ABIERTO
  // ══════════════════════════════════════════════════════════
  if (estudiantesAbierto.length > 0) {
    addSection(`CURSO ABIERTO - ESTUDIANTES (${estudiantesAbierto.length} registros)`, 7);
    rows.push([
      colHeader("Nombre"), colHeader("Email"), colHeader("Empresa"),
      colHeader("Vendedor"), colHeader("Ingreso"), colHeader("Moneda"), colHeader("USD Equiv."),
    ]);
    r++;

    estudiantesAbierto.forEach((ea) => {
      rows.push([
        valCell(ea.nombre || ""),
        valCell(ea.email || ""),
        valCell(ea.empresa || ""),
        valCell(ea.vendedor || ""),
        numCell(ea.ingreso || ea.precio || 0),
        valCell(ea.moneda || ""),
        valCell(""),
      ]);
      r++;
    });

    rows.push(totalRowStyled(["TOTAL", "", "", "", detalle.ingreso_abierto || 0, "USD", ""], LSSI_BLUE));
    r++;
    addBlank();
  }

  // ══════════════════════════════════════════════════════════
  // 8. INGRESO GENERAL
  // ══════════════════════════════════════════════════════════
  if (ingresoGeneral.length > 0) {
    addSection(`INGRESO GENERAL (${ingresoGeneral.length} registros)`, 4);
    rows.push([colHeader("Descripción"), colHeader("Partner"), colHeader("Monto"), colHeader("Moneda")]);
    r++;
    ingresoGeneral.forEach((ig) => {
      rows.push([
        valCell(ig.descripcion || ig.concepto || ""),
        valCell(ig.partner || ""),
        numCell(ig.monto || ig.cantidad || 0),
        valCell(ig.moneda || ""),
      ]);
      r++;
    });
    addBlank();
  }

  // ══════════════════════════════════════════════════════════
  // 9. COMISIÓN DE VENTA
  // ══════════════════════════════════════════════════════════
  if (comisionVenta.length > 0) {
    addSection(`COMISIÓN DE VENTA (${comisionVenta.length} registros)`, 5);
    rows.push([colHeader("Descripción"), colHeader("Partner"), colHeader("Vendedor"), colHeader("Monto"), colHeader("Moneda")]);
    r++;
    comisionVenta.forEach((cv) => {
      rows.push([
        valCell(cv.descripcion || ""),
        valCell(cv.partner || ""),
        valCell(cv.vendedor || ""),
        numCell(cv.monto || cv.cantidad || 0),
        valCell(cv.moneda || ""),
      ]);
      r++;
    });
    addBlank();
  }

  // ══════════════════════════════════════════════════════════
  // 10. PROYECTO CON CAUSA
  // ══════════════════════════════════════════════════════════
  if (proyectoConCausa.length > 0) {
    addSection(`PROYECTO CON CAUSA (${proyectoConCausa.length} registros)`, 5);
    rows.push([colHeader("Descripción"), colHeader("Partner"), colHeader("Causa"), colHeader("Monto"), colHeader("Moneda")]);
    r++;
    proyectoConCausa.forEach((pc) => {
      rows.push([
        valCell(pc.descripcion || ""),
        valCell(pc.partner || ""),
        valCell(pc.causa || ""),
        numCell(pc.monto || pc.cantidad || 0),
        valCell(pc.moneda || ""),
      ]);
      r++;
    });
    addBlank();
  }

  // ══════════════════════════════════════════════════════════
  // 11. REPARTO DE UTILIDADES
  // ══════════════════════════════════════════════════════════
  addSection("REPARTO DE UTILIDADES", 6);

  const dist = calculateDistribution({
    utilidad,
    utilities,
    rawUtilidades,
    instructores,
    filteredLider,
    proyectoLider: selectedProyecto?.lider,
    loadedLeader,
  });

  rows.push([
    labelCell("Utilidad Total"),
    numCell(dist.utilidad, LSSI_TEAL),
    valCell("USD"),
    showDualCurrency && origProfit ? currencyCell(origProfit, pCur) : valCell(""),
    valCell(""), valCell(""),
  ]);
  r++;
  addBlank();

  // ── 11a. DETALLE DE ETAPAS ──
  if (dist.stageBreakdown.length > 0) {
    // Define the correct display order matching the UI
    const STAGE_ORDER = [
      "Contacto", "Cita",                           // Preventa
      "Diagnóstico", "Propuesta", "Cierre",          // Comercial
      "Kickoff", "Seguimiento", "Reunión de Cierre", // Post Venta
      "Coaching/Líder",                               // Coaching
    ];

    // Sort stageBreakdown to match UI order
    const sortedStages = [...dist.stageBreakdown].sort((a, b) => {
      const aIdx = STAGE_ORDER.indexOf(a.stage);
      const bIdx = STAGE_ORDER.indexOf(b.stage);
      return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
    });

    // Category row colors (light pastel backgrounds matching the UI)
    const CAT_ROW_COLORS = {
      Preventa:    { bg: "DBEAFE", fg: "1E40AF" }, // light blue bg, dark blue text
      Comercial:   { bg: "D1FAE5", fg: "065F46" }, // light green bg, dark green text
      "Post Venta": { bg: "FEF3C7", fg: "92400E" }, // light yellow bg, dark amber text
      Coaching:    { bg: "CCFBF1", fg: "115E59" }, // light teal bg, dark teal text
    };

    const cols = showDualCurrency ? 6 : 5;
    addSub(`📌 DETALLE DE ETAPAS (${formatNumber(dist.totalStagesPercent)}%)`, LSSI_PURPLE, cols);

    const headers = [
      colHeader("Categoría"), colHeader("Etapa"), colHeader("Partner Asignado"),
      colHeader("%"),
    ];
    if (showDualCurrency) {
      headers.push(colHeader(`Monto ${pCur}`));
      headers.push(colHeader("Monto USD"));
    } else {
      headers.push(colHeader("Monto USD"));
    }
    rows.push(headers);
    r++;

    const origUtilidad = origProfit || dist.utilidad;

    sortedStages.forEach((sb) => {
      const colors = CAT_ROW_COLORS[sb.category] || { bg: "F3F4F6", fg: "333333" };
      const origAmount = (sb.percent / 100) * origUtilidad;

      const row = [
        {
          v: sb.category,
          s: {
            font: { bold: true, color: { rgb: colors.fg }, sz: 10 },
            fill: { fgColor: { rgb: colors.bg } },
            alignment: { horizontal: "center", vertical: "center" },
          },
        },
        {
          v: sb.stage,
          s: {
            font: { sz: 10 },
            fill: { fgColor: { rgb: colors.bg } },
            alignment: { horizontal: "left", vertical: "center" },
          },
        },
        {
          v: sb.partner,
          s: {
            font: { sz: 10 },
            fill: { fgColor: { rgb: colors.bg } },
            alignment: { horizontal: "left", vertical: "center" },
          },
        },
        {
          v: Number(sb.percent) || 0,
          t: "n",
          s: {
            font: { bold: true, sz: 10 },
            fill: { fgColor: { rgb: colors.bg } },
            numFmt: '0.00"%"',
            alignment: { horizontal: "right", vertical: "center" },
          },
        },
      ];

      if (showDualCurrency) {
        row.push({
          v: Number(origAmount) || 0,
          t: "n",
          s: {
            font: { sz: 10 },
            fill: { fgColor: { rgb: colors.bg } },
            numFmt: "#,##0.00",
            alignment: { horizontal: "right", vertical: "center" },
          },
        });
      }
      row.push({
        v: Number(sb.amount) || 0,
        t: "n",
        s: {
          font: { sz: 10 },
          fill: { fgColor: { rgb: colors.bg } },
          numFmt: '"$"#,##0.00',
          alignment: { horizontal: "right", vertical: "center" },
        },
      });

      rows.push(row);
      r++;
    });

    // Subtotal Etapas
    const origStageTotal = (dist.totalStagesPercent / 100) * origUtilidad;
    const subtotalRow = [
      {
        v: "Subtotal Etapas",
        s: { font: { bold: true, sz: 11 }, fill: { fgColor: { rgb: "E5E7EB" } }, alignment: { horizontal: "left" } },
      },
      { v: "", s: { fill: { fgColor: { rgb: "E5E7EB" } } } },
      { v: "", s: { fill: { fgColor: { rgb: "E5E7EB" } } } },
      {
        v: Number(dist.totalStagesPercent) || 0,
        t: "n",
        s: { font: { bold: true, sz: 11 }, fill: { fgColor: { rgb: "E5E7EB" } }, numFmt: '0.00"%"', alignment: { horizontal: "right" } },
      },
    ];

    if (showDualCurrency) {
      subtotalRow.push({
        v: Number(origStageTotal) || 0,
        t: "n",
        s: {
          font: { bold: true, sz: 11, color: { rgb: LSSI_TEAL } },
          fill: { fgColor: { rgb: "E5E7EB" } },
          numFmt: "#,##0.00",
          alignment: { horizontal: "right" },
        },
      });
    }
    subtotalRow.push({
      v: Number(dist.totalStages) || 0,
      t: "n",
      s: {
        font: { bold: true, sz: 11, color: { rgb: LSSI_TEAL } },
        fill: { fgColor: { rgb: "E5E7EB" } },
        numFmt: '"$"#,##0.00',
        alignment: { horizontal: "right" },
      },
    });

    rows.push(subtotalRow);
    r++;
    addBlank();
  }

  // ── 11b. ASIGNACIÓN DE INSTRUCTORES ──
  if (dist.assignments.length > 0) {
    addSub(`ASIGNACIÓN DE INSTRUCTORES (${formatNumber(dist.totalAssignmentsPercent)}%)`, LSSI_ORANGE, 6);
    rows.push([
      colHeader("Partner"), colHeader("Ingreso Generado"),
      colHeader("Proporción %"), colHeader("Comisión USD"),
      colHeader(showDualCurrency ? `Comisión ${pCur}` : ""), colHeader("% de Utilidad"),
    ]);
    r++;

    const origUtilidadAssign = origProfit || dist.utilidad;

    dist.assignments.forEach((a) => {
      const origCommission = (a.percentOfUtilidad / 100) * origUtilidadAssign;
      rows.push([
        valCell(a.partner),
        numCell(a.income),
        pctCell(a.proportion),
        numCell(a.commission),
        showDualCurrency ? numCell(origCommission) : valCell(""),
        pctCell(a.percentOfUtilidad),
      ]);
      r++;
    });

    const origAssignTotal = (dist.totalAssignmentsPercent / 100) * (origProfit || dist.utilidad);
    rows.push(totalRowStyled([
      "Total Asignación",
      dist.totalAssignments,
      "", "",
      showDualCurrency ? origAssignTotal : "",
      `${formatNumber(dist.totalAssignmentsPercent)}%`,
    ], LSSI_ORANGE));
    r++;
    addBlank();
  }

  // ── 11c. RESUMEN POR PARTNER ──
  const partnerKeys = Object.keys(dist.partnerSummary);
  if (partnerKeys.length > 0) {
    addSub("RESUMEN POR PARTNER", LSSI_GREEN, 6);
    rows.push([
      colHeader("Partner"), colHeader("Pipeline USD"), colHeader("Asignación USD"),
      colHeader("Total USD"), colHeader(showDualCurrency ? `Total ${pCur}` : ""), colHeader("% de Utilidad"),
    ]);
    r++;

    const origUtilidadSummary = origProfit || dist.utilidad;

    partnerKeys.forEach((partner) => {
      const p = dist.partnerSummary[partner];
      const origTotal = (p.grandPercent / 100) * origUtilidadSummary;
      rows.push([
        valCell(partner),
        numCell(p.stageTotal),
        numCell(p.assignmentTotal),
        numCell(p.grandTotal),
        showDualCurrency ? numCell(origTotal) : valCell(""),
        pctCell(p.grandPercent),
      ]);
      r++;
    });

    const origPartnersTotal = (dist.totalPartnersPercent / 100) * (origProfit || dist.utilidad);
    rows.push(totalRowStyled([
      "Total Partners", "", "",
      dist.totalPartnersAmount,
      showDualCurrency ? origPartnersTotal : "",
      `${formatNumber(dist.totalPartnersPercent)}%`,
    ], LSSI_GREEN));
    r++;

    const origLSSI = (dist.lssiRemainingPercent / 100) * (origProfit || dist.utilidad);
    rows.push(totalRowStyled([
      "LSSI", "", "",
      dist.lssiRemaining,
      showDualCurrency ? origLSSI : "",
      `${formatNumber(dist.lssiRemainingPercent)}%`,
    ], LSSI_BLUE));
    r++;
    addBlank();
  }

  // ── 11d. RESUMEN FINAL: PARTNERS vs LSSI ──
  // Mirrors the UI cards "TOTAL PARTNERS (excl. LSSI)" and "TOTAL LSSI"
  // so the export answers "how much goes to partners vs LSSI" at a glance.
  {
    const cols = showDualCurrency ? 4 : 3;
    addSub("RESUMEN FINAL: PARTNERS vs LSSI", LSSI_BLUE, cols);

    const headers = [
      colHeader("Concepto"),
      colHeader("Monto USD"),
    ];
    if (showDualCurrency) headers.push(colHeader(`Monto ${pCur}`));
    headers.push(colHeader("% de Utilidad"));
    rows.push(headers);
    r++;

    const origTotalPartners = (dist.totalPartnersPercent / 100) * (origProfit || dist.utilidad);
    const partnersRow = ["TOTAL PARTNERS (excl. LSSI)", dist.totalPartnersAmount];
    if (showDualCurrency) partnersRow.push(origTotalPartners);
    partnersRow.push(`${formatNumber(dist.totalPartnersPercent)}%`);
    rows.push(totalRowStyled(partnersRow, LSSI_GREEN));
    r++;

    const origLSSITotal = (dist.lssiRemainingPercent / 100) * (origProfit || dist.utilidad);
    const lssiRow = ["TOTAL LSSI", dist.lssiRemaining];
    if (showDualCurrency) lssiRow.push(origLSSITotal);
    lssiRow.push(`${formatNumber(dist.lssiRemainingPercent)}%`);
    rows.push(totalRowStyled(lssiRow, LSSI_TEAL));
    r++;
    addBlank();
  }

  // ══════════════════════════════════════════════════════════
  // 12. FOOTER
  // ══════════════════════════════════════════════════════════
  addBlank();
  rows.push([labelCell("Generado"), valCell(new Date().toLocaleString("es-MX"))]);
  r++;
  rows.push([labelCell("Sistema"), valCell("LSSI - Detalle de Proyecto")]);
  r++;

  // ══════════════════════════════════════════════════════════
  // BUILD WORKBOOK
  // ══════════════════════════════════════════════════════════
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!merges"] = merges;

  ws["!cols"] = [
    { wch: 24 }, { wch: 24 }, { wch: 20 }, { wch: 18 },
    { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 14 },
    { wch: 16 }, { wch: 16 }, { wch: 12 },
  ];

  const wb = XLSX.utils.book_new();
  const sheetName = (selectedProyecto.proyecto_code || "Proyecto").substring(0, 31);
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  const fileName = `proyecto_${selectedProyecto.proyecto_code}_${new Date().toISOString().split("T")[0]}.xlsx`;
  XLSX.writeFile(wb, fileName);

  toast.success("✅ Reporte Excel generado exitosamente");
}